import React from 'react'
import { useTranslation } from 'react-i18next'
import { every } from 'lodash'
import { DateTime } from 'luxon'
import { NodeHtmlMarkdown } from 'node-html-markdown'
import { BeWizrLearningTrack, BeWizrLearningTrackItem, RemoteImage } from '~/models'
import { observer } from '~/ui/component'
import {
  Empty,
  HBox,
  Hero,
  Label,
  Markdown,
  Panel,
  SVG,
  VBox,
} from '~/ui/components'
import { SVGName } from '~/ui/components/SVG'
import Timeline, { TimelineItem } from '~/ui/components/Timeline'
import { createUseStyles, layout } from '~/ui/styling'
import { BeWizrEnrollmentStatus, BeWizrSection, rememberLearningTrackForCourse } from '../bewizr'
import BeWizrCourseTile from '../bewizr-courses/BeWizrCourseTile'
import BeWizrMeetingTile from '../bewizr-meetings/BeWizrMeetingTile'
import BeWizrLearningTrackEnrollmentPanel from './BeWizrLearningTrackEnrollmentPanel'

export interface Props {
  learningTrack: BeWizrLearningTrack
}

const BeWizrLearningTrackView = observer('BeWizrLearningTrackView', (props: Props) => {

  const [t, i18next] = useTranslation('bewizr-learning-tracks')

  const {learningTrack} = props

  const description = React.useMemo(() => {
    if (learningTrack.descriptionHtml == null || learningTrack.descriptionHtml.length === 0) { return null }
    // A little bit ridiculous, but this way we can use the styling from the <Markdown/> component
    return NodeHtmlMarkdown.translate(learningTrack.descriptionHtml)
  }, [learningTrack.descriptionHtml])

  const image: RemoteImage | undefined = learningTrack.links.image == null ? undefined : {
    type: 'remote',
    url:   learningTrack.links.image,
  }

  //------
  // Enrollment

  const enrolled      = learningTrack.isEnrolled
  const onWaitingList = learningTrack.isOnWaitingList
  const mayEnroll     = learningTrack.mayEnroll
  const mayCancel     = learningTrack.mayCancel

  // Remember which learning track each course was opened from, so the course screen can show
  // a way back after the user returns from the (separate-tab) course content.
  React.useEffect(() => {
    const context = {id: learningTrack.id, title: learningTrack.title}
    learningTrack.items?.forEach(item => {
      if (item.type === 'course') {
        rememberLearningTrackForCourse(item.slug, context)
      }
    })
  }, [learningTrack])

  //------
  // Rendering

  const $ = useStyles()

  const renderItem = React.useCallback((item: BeWizrLearningTrackItem) => {
    if (item.type === 'course') {
      return (
        <BeWizrCourseTile
          title={item.title}
          slug={item.slug}
          progress={item.progress}
          image={item.links.image}
          locked={!enrolled || item.isLocked}
          horizontal
        />
      )
    } else {
      return (
        <BeWizrMeetingTile
          meetingID={item.meetingId}
          occurrenceID={item.id}
          title={item.title}
          image={item.links.image}
          startDate={item.startDate}
          endDate={item.endDate}
          locked={!enrolled || item.isLocked}
          horizontal
        />
      )
    }
  }, [enrolled])

  const items = React.useMemo(() => {
    const now = DateTime.now()

    const items: TimelineItem[] = [{
      caption:   t('start'),
      detail:    enrolled ? learningTrack.startDate?.toFormat('d-M-yyyy', {locale: i18next.language}) : null,
      completed: enrolled && (learningTrack.startDate == null || learningTrack.startDate <= now),
    }]

    learningTrack.items?.forEach(item => {
      items.push({
        render:    () => renderItem(item),
        completed: item.type === 'course' ? item.progress === 100 : item.endDate != null && item.endDate < now,
      })
    })

    items.push({
      icon:      'finish-flag',
      detail:    learningTrack.endDate?.toFormat('d-M-yyyy', {locale: i18next.language}),
      completed: every(items, it => it.completed),
    })

    return items
  }, [learningTrack.startDate, learningTrack.endDate, learningTrack.items, renderItem, t, i18next, enrolled])

  function render() {
    return (
      <VBox classNames={$.BeWizrLearningTrackView}>
        <Hero
          title={renderSummary()}
          image={image}
          backgroundImage={image}
          compact
        />
        <VBox align='center' padding={layout.padding.m}>
          {renderContent()}
        </VBox>
      </VBox>
    )
  }

  function renderSummary() {
    return (
      <VBox gap={layout.padding.l}>
        <VBox gap={layout.padding.inline.m} align='left'>
          <Label h1 truncate={false}>
            {learningTrack.title}
          </Label>
          {renderStatus()}
        </VBox>
        <VBox gap={layout.padding.inline.m}>
          {renderSummaryItem(
            'unordered-list',
            t('items.count', {count: learningTrack.itemCount}),
          )}
          {learningTrack.startDate != null && (
            <>
              {renderSummaryItem(
                'calendar',
                learningTrack.startDate.toFormat('d-M-yyyy', {locale: i18next.language}),
              )}
              {renderSummaryItem(
                'clock',
                learningTrack.startDate.toFormat('HH:mm', {locale: i18next.language}),
              )}
            </>
          )}
        </VBox>
      </VBox>
    )
  }

  function renderStatus() {
    if (!enrolled && !onWaitingList && !mayEnroll) { return null }

    return (
      <BeWizrEnrollmentStatus
        isEnrolled={enrolled}
        isOnWaitingList={onWaitingList}
        placesLeft={learningTrack.placesLeft}
      />
    )
  }

  function renderSummaryItem(icon: SVGName, caption: string) {
    return (
      <HBox gap={layout.padding.inline.m}>
        <SVG name={icon} size={layout.icon.m}/>
        <Label>
          {caption}
        </Label>
      </HBox>
    )
  }

  function renderContent() {
    return (
      <VBox classNames={$.content} gap={layout.padding.xl} paddingVertical={layout.padding.l}>
        <BeWizrSection title={t('description')}>
          {renderDescription()}
        </BeWizrSection>
        {(mayEnroll || mayCancel) && (
          <BeWizrSection title={t('enrollment.title')}>
            <BeWizrLearningTrackEnrollmentPanel id={learningTrack.id}/>
          </BeWizrSection>
        )}
        <BeWizrSection title={t('items.title')}>
          {items.length === 2 ? (
            <Panel>
              <Empty
                {...t('items.empty')}
                padded
              />
            </Panel>
          ) : (
            <Timeline items={items} large/>
          )}
        </BeWizrSection>
      </VBox>
    )
  }

  function renderDescription() {
    if (description == null) { return null }

    return (
      <Markdown>
        {description}
      </Markdown>
    )
  }

  return render()

})

export default BeWizrLearningTrackView

const useStyles = createUseStyles({
  BeWizrLearningTrackView: {
  },

  content: {
    width:    layout.contentWidth,
    maxWidth: '100%',
  },
})